/**
 * BED — Clôture d'édition (migration 00233, cahier L53-54 + L123).
 *
 * Décision d'humain, donc trois temps stricts :
 *   1. l'admin choisit une édition ;
 *   2. il obtient une PRÉVISUALISATION CHIFFRÉE par catégorie — impactés ET
 *      non impactés ;
 *   3. il confirme explicitement, et seulement alors ça s'exécute.
 *
 * Aucune de ces trois étapes ne calcule quoi que ce soit côté client : les
 * chiffres viennent de `bed_close_edition_preview`, le résultat de
 * `bed_close_edition_execute`.
 */

import { useCallback, useEffect, useState } from 'react';
import { Archive, MailX, RefreshCw } from 'lucide-react';
import { Button, ConfirmDialog, Select, toast } from '../ui';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import {
  bedAutomationErrorMessage,
  executeBedEditionClosure,
  previewBedEditionClosure,
  type BedClosurePreview,
  type BedClosureResult,
} from '../../lib/bed/automations';

/** Miroir serveur de bed_status_label, pour le bloc « non impactés ». */
const STATUS_LABELS: Record<string, string> = {
  bpi_payment_pending: 'BPI en attente',
  pre_registered: 'Pré-inscrit',
  bed_downloaded: 'BED téléchargé',
  registered: 'Inscrit',
  bed_incomplete: 'BED incomplet — infos',
  bed_incomplete_payment: 'BED incomplet — paiement',
  bed_complete: 'BED complet',
  completed: 'Inscrit définitif',
  cancellation_compliant: 'Annulation conforme',
  cancellation_non_compliant: 'Annulation non conforme',
  postponement_compliant: 'Report conforme',
  postponement_non_compliant: 'Report non conforme',
  archived: 'Archivé',
};

export function BedEditionClosureCard() {
  const { products, loading: productsLoading } = useProductsSelect();
  const [productId, setProductId] = useState('');
  const [preview, setPreview] = useState<BedClosurePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<BedClosureResult | null>(null);

  const loadPreview = useCallback(async (id: string) => {
    if (!id) {
      setPreview(null);
      return;
    }
    setLoading(true);
    const res = await previewBedEditionClosure(id);
    if (res.status === 'ok') {
      setPreview(res.data);
    } else {
      setPreview(null);
      toast.error(bedAutomationErrorMessage(res.status));
    }
    setLoading(false);
  }, []);

  // Le résultat d'une exécution précédente n'a plus de sens dès qu'on change
  // d'édition : on le jette avec la prévisualisation.
  useEffect(() => {
    setResult(null);
    loadPreview(productId);
  }, [productId, loadPreview]);

  async function handleExecute() {
    setConfirmOpen(false);
    setLoading(true);
    const res = await executeBedEditionClosure(productId);
    if (res.status === 'ok') {
      setResult(res.data);
      setPreview(res.data.preview_after);
      toast.success(
        `Édition clôturée : ${res.data.cancelled_compliant} annulation(s) conforme(s), ` +
          `${res.data.cancelled_non_compliant} non conforme(s)`
      );
    } else {
      toast.error(bedAutomationErrorMessage(res.status));
    }
    setLoading(false);
  }

  const impacted = preview?.total_impacted ?? 0;

  return (
    <section className="flex flex-col gap-4 px-page-padding py-6">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-900">
          <Archive className="size-4 text-zinc-500" aria-hidden />
          Clôture d'édition
        </h2>
        <p className="max-w-3xl text-sm leading-5 text-zinc-500">
          En fin d'édition, les équipages restés « BED téléchargé » passent en{' '}
          <strong>annulation conforme</strong> et les équipages encore « pré-inscrits » en{' '}
          <strong>annulation non conforme, sans email</strong> ; leurs contacts deviennent de
          simples clients. Les annulations, reports et inscriptions définitives déjà posés ne sont
          jamais touchés.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Édition</span>
          <Select
            className="w-72"
            value={productId}
            disabled={productsLoading || loading}
            onChange={(e) => setProductId(e.target.value)}
            options={products.map((p) => ({ value: p.id, label: p.name }))}
            placeholder="— choisir une édition —"
          />
        </label>
        <Button
          variant="outline"
          disabled={!productId || loading}
          onClick={() => loadPreview(productId)}
        >
          <RefreshCw className="size-4" aria-hidden />
          Recalculer
        </Button>
        <Button
          variant="destructive"
          disabled={!productId || loading || impacted === 0}
          onClick={() => setConfirmOpen(true)}
        >
          Clôturer l'édition
        </Button>
      </div>

      {preview && (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4">
              <p className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                {preview.to_cancellation_compliant.label}
              </p>
              <p className="mt-1 text-2xl font-semibold text-zinc-900">
                {preview.to_cancellation_compliant.count}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                → Annulation conforme · email de confirmation envoyé
              </p>
            </div>
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4">
              <p className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                {preview.to_cancellation_non_compliant.label}
              </p>
              <p className="mt-1 text-2xl font-semibold text-zinc-900">
                {preview.to_cancellation_non_compliant.count}
              </p>
              <p className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
                <MailX className="size-3.5" aria-hidden />→ Annulation non conforme · aucun email ·{' '}
                {preview.contacts_to_client} contact(s) passent en clients
              </p>
            </div>
          </div>

          {Object.keys(preview.untouched).length > 0 && (
            <div className="rounded-md border border-zinc-200 p-4">
              <p className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Non impactés
              </p>
              <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                {Object.entries(preview.untouched)
                  .sort(([, a], [, b]) => b - a)
                  .map(([status, count]) => (
                    <li key={status} className="text-sm text-zinc-600">
                      {STATUS_LABELS[status] ?? status} :{' '}
                      <span className="font-semibold text-zinc-900">{count}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {impacted === 0 && (
            <p className="text-sm text-zinc-500">Aucun équipage à clôturer sur cette édition.</p>
          )}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900">
          <p className="font-semibold">Clôture exécutée — {result.product_name}</p>
          <ul className="mt-1 list-inside list-disc">
            <li>{result.cancelled_compliant} équipage(s) passés en annulation conforme</li>
            <li>
              {result.cancelled_non_compliant} équipage(s) passés en annulation non conforme (sans
              email)
            </li>
            <li>{result.contacts_switched_to_client} contact(s) passés en clients</li>
          </ul>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Clôturer cette édition ?"
        description={
          preview
            ? `${preview.to_cancellation_compliant.count} équipage(s) « BED téléchargé » passeront en annulation conforme (email envoyé), et ${preview.to_cancellation_non_compliant.count} équipage(s) « pré-inscrits » en annulation non conforme sans email — ${preview.contacts_to_client} contact(s) deviendront des clients. Cette action est tracée mais n'est pas annulable en un clic.`
            : ''
        }
        confirmLabel="Clôturer l'édition"
        variant="destructive"
        onConfirm={handleExecute}
      />
    </section>
  );
}
